import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppOutbox } from "./AppOutbox";
import { AppComposerView, IComposerSent } from "./AppComposer";
import { AppAttachRules } from "./AppAttachRules";
import { kBlobBytes } from "../../../Admin/ReUsableTS/kBlobBytes";

export interface IAttachmentComposerHost {
    HoldAttachment(Then: IComposerSent): string;
    QueueAttachment(Data: { name: string; mime: string; data: string; voice: boolean }, Caption: string,
                    Href: string, Then: IComposerSent): string;
    SendSay(Text: string, Bad: boolean): void;
    AttachmentQueued(): void;
    ReleaseAttachment(Then: IComposerSent): void;
}

/** File selection never sends. The existing Send click explicitly approves one selected file. */
export class AppAttachmentComposer implements IComposerSent {
    private Host: IAttachmentComposerHost;
    private Input: HTMLInputElement;
    private Voice: HTMLInputElement;
    private Timer: number = 0;
    private Seconds: number = 0;

    public constructor(Host: IAttachmentComposerHost, Input: HTMLInputElement, Voice: HTMLInputElement) {
        this.Host = Host; this.Input = Input; this.Voice = Voice;
        this.Wire();
    }

    public HasFile = (): boolean => this.Input.files !== null && this.Input.files.length > 0;

    /**
     * ⭐ THE ROW SHOWS WHAT WAS CHOSEN (audit 2026-10-12): the picker's `change` paints the file's name and size and
     * the button that takes it away; a file that cannot go is refused HERE, not after Send. ⛔ Nothing is sent.
     */
    private Wire = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Input.addEventListener("change", this.OnChosen);
            const Clear: HTMLElement | null = document.getElementById(AppComposerView.AttachClearId);
            if (Clear !== null) { Clear.addEventListener("click", this.OnClear); }
            const Box: HTMLElement | null = document.getElementById("wac-text");
            if (Box !== null) { Box.addEventListener("paste", this.OnPaste as EventListener); }
            this.Show();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAttachmentComposer.Wire");
        }
    };

    private OnChosen = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.HasFile() === true && AppAttachRules.SizeOk((this.Input.files as FileList)[0].size, AppOutbox.MaxAttachmentBytes) === false) {
                this.Input.value = "";
                this.Host.SendSay(AppAttachRules.TooBigWhy(AppOutbox.MaxAttachmentBytes), true);
            } else if (this.HasFile() === true) {
                this.Host.SendSay("", false);
            }
            this.Show();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAttachmentComposer.OnChosen");
        }
    };

    /**
     * ⭐ PASTE (owner 2026-10-23): Ctrl+V of a screenshot or a copied file into the message box puts it in the picker,
     * exactly as if it had been chosen - same size check, same row, and ⛔ it goes only when Send is pressed. A paste
     * that carries text (cells from Excel also carry a picture) stays a text paste. One file: the first one wins.
     */
    private OnPaste = (Evt: ClipboardEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Data: DataTransfer | null = Evt.clipboardData;
            if (Data === null) { return; }
            if (Data.getData("text/plain").length > 0) { return; }
            let Got: File | null = null;
            for (let i: number = 0; i < Data.items.length && Got === null; i++) {
                if (Data.items[i].kind === "file") { Got = Data.items[i].getAsFile(); }
            }
            if (Got === null) { return; }
            Evt.preventDefault();
            const Named: File = new File([Got], AppAttachRules.PastedName(Got.name, Got.type, new Date()), { type: Got.type });
            const Carrier: DataTransfer = new DataTransfer();
            Carrier.items.add(Named);
            this.Input.files = Carrier.files;
            this.OnChosen();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAttachmentComposer.OnPaste");
        }
    };

    /** The owner took the file away again: the picker is emptied and gets the focus back (its button would be gone). */
    private OnClear = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Input.value = "";
            this.Voice.checked = false;
            this.Show();
            this.Input.focus();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAttachmentComposer.OnClear");
        }
    };

    /** Paints the name, the remove button and the voice-note choice from the picker's own state. Text only. */
    private Show = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Name: HTMLElement | null = document.getElementById(AppComposerView.AttachNameId);
            const Clear: HTMLElement | null = document.getElementById(AppComposerView.AttachClearId);
            const Wrap: HTMLElement | null = document.getElementById(AppComposerView.VoiceWrapId);
            let Words: string = "";
            let Audio: boolean = false;
            if (this.HasFile() === true) {
                const Chosen: File = (this.Input.files as FileList)[0];
                Words = AppAttachRules.Shown(Chosen.name, Chosen.size);
                Audio = AppAttachRules.IsAudio(Chosen.type);
            }
            if (Name !== null) { Name.textContent = Words; Name.hidden = Words.length === 0; }
            if (Clear !== null) { Clear.hidden = Words.length === 0; }
            if (Wrap !== null) { Wrap.hidden = Audio === false; }
            if (Audio === false) { this.Voice.checked = false; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAttachmentComposer.Show");
        }
    };

    public Send = (Caption: string, Href: string): void => {
        if (getCoreUtils().KErrorMode || !this.HasFile()) { return; }
        const File: File = (this.Input.files as FileList)[0];
        if (AppAttachRules.SizeOk(File.size, AppOutbox.MaxAttachmentBytes) === false) {
            this.Host.SendSay(AppAttachRules.TooBigWhy(AppOutbox.MaxAttachmentBytes), true); return;
        }
        const Voice: boolean = this.Voice.checked;
        if (Voice && (Caption.length > 0 || !File.type.startsWith("audio/"))) {
            this.Host.SendSay(AppAttachRules.VoiceWhy, true); return;
        }
        const Why: string = this.Host.HoldAttachment(this);
        if (Why.length > 0) { this.Host.SendSay(Why, true); return; }
        this.Seconds = 0;
        this.Tick();
        this.Timer = window.setInterval(this.Tick, 1000);
        const Self: AppAttachmentComposer = this;
        function OnLoaded(Url: string): void { Self.Loaded(Url, File, Caption, Href, Voice); }
        kBlobBytes.ReadDataUrl(File, OnLoaded, this.Failed);
    };

    private Loaded = (Url: string, File: File, Caption: string, Href: string, Voice: boolean): void => {
        const Data: string = Url.substring(Url.indexOf(",") + 1);
        const Why: string = this.Host.QueueAttachment({ name: File.name, mime: File.type || "application/octet-stream",
            data: Data, voice: Voice }, Caption, Href, this);
        if (Why.length > 0) { this.Failed(Why); }
    };

    private Tick = (): void => {
        this.Host.SendSay("Preparing attachment… " + String(this.Seconds++) + "s", false);
    };

    public Queued = (): void => {
        this.Stop(); this.Input.value = ""; this.Voice.checked = false;
        this.Show();
        this.Host.AttachmentQueued();
    };

    public Failed = (Why: string): void => {
        this.Stop(); this.Host.ReleaseAttachment(this); this.Host.SendSay(Why, true);
    };

    private Stop = (): void => {
        if (this.Timer !== 0) { window.clearInterval(this.Timer); this.Timer = 0; }
    };
}
